import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { constSelector, useRecoilValue } from 'recoil';
import { DateTime } from 'luxon';
import { NumberParam, useQueryParam, } from 'use-query-params';
import { Box } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { typeDate } from 'components/mui/Formatters';
import { MemoizedColumnHeaders, MemoizedRow } from 'components/mui/Memoized';
import { CarModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import AlbumButton from 'modules/cars/common/AlbumButton';
import CopyableChassis from 'modules/cars/common/CopyableChassis';
import VoyageLink from 'modules/cars/common/VoyageLink';
import useCarsPaginated from 'modules/cars/hooks/useCarsPaginated';
import auth from 'recoil/atoms/auth';
import companyList from 'recoil/atoms/companyList';


const ShippedInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const companies = useRecoilValue(my.type === CompanyType.Shipper ? constSelector([]) : companyList(CompanyType.Shipper));
    const [shipper] = useQueryParam('shipper', NumberParam);
    const {cars, total, loading, setPage, setFilter} = useCarsPaginated(
        StockStatus.Shipped,
        100,
        shipper ? [{field: 'company_id', operator: 'is', value: shipper}] : undefined
    );

    const columns: GridColDef<CarModel>[] = [
        ...my.type === CompanyType.Master ? [{
            field: 'company_id',
            headerName: t('shipper'),
            valueGetter: params => params.row.company?.id,
            renderCell: params => params.row.company?.name,
            type: 'singleSelect',
            valueOptions: companies.map(s => ({
                value: s.id,
                label: s.name,
            })),
            flex: 1,
            minWidth: 120,
        }] as GridColDef<CarModel>[] : [],
        {
            field: 'model',
            headerName: t('model'),
            valueGetter: params => `${params.row.make} ${params.row.model}`,
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'chassisNumber',
            headerName: t('chassis'),
            renderCell: params => <CopyableChassis car={params.row}/>,
            width: 210,
        },
        {
            field: 'voyage',
            headerName: t('voyage'),
            valueGetter: params => params.row.voyage!.id,
            renderCell: params => <VoyageLink voyage={params.row.voyage!}/>,
            width: 130,
        },
        {
            field: 'etd',
            headerName: t('etd'),
            flex: 1,
            minWidth: 100,
            ...typeDate,
            valueGetter: params => DateTime.fromISO(params.row.voyage!.etd),
        },
        {
            field: 'eta',
            headerName: t('eta'),
            flex: 1,
            minWidth: 100,
            ...typeDate,
            valueGetter: params => DateTime.fromISO(params.row.voyage!.eta),
        },
        {
            field: 'docs',
            headerName: t('bl_issue'),
            flex: 1,
            minWidth: 100,
            ...typeDate,
            valueGetter: params => DateTime.fromISO(params.row.voyage!.blIssueDate),
        },
        {
            field: 'photos',
            headerName: t('photos'),
            renderCell: params => <AlbumButton car={params.row}/>,
            width: 140,
            filterable: false,
            sortable: false,
        },
    ];

    return (
        <Box sx={{height: 'calc(100vh - 148px)', bgcolor: 'background.paper'}}>
            <DataGrid
                loading={loading}
                rowCount={total}
                pageSizeOptions={[100]}
                paginationMode="server"
                onPaginationModelChange={setPage}
                filterMode="server"
                onFilterModelChange={setFilter}
                rows={cars}
                columns={columns}
                disableRowSelectionOnClick
                slots={{
                    row: MemoizedRow,
                    columnHeaders: MemoizedColumnHeaders,
                }}
                filterModel={shipper ? {
                    items: [{field: 'company_id', operator: 'is', value: shipper}],
                } : undefined}
            />
        </Box>
    );
};

const Shipped = () => {
    return (
        <Suspense fallback={null}>
            <ShippedInner/>
        </Suspense>
    )
}

export default Shipped;
